import clsx from 'clsx';
import { forwardRef, type ReactNode, type ReactElement } from 'react';
import { isElement } from 'react-is';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { _textStyleCSS as textStyleCSS } from '@dynatrace/strato-components/styles';

import { footerCSS } from './Footer.sty.js';
import { footerSlotCss } from '../EmptyState.sty.js';

//#region Typings
/**
 * @public
 */
export interface EmptyStateFooterProps
  extends WithChildren,
    StylingProps,
    DataTestId {}
//#endregion Typings

/**
 * Typeguard function that will check if a ReactNode is a EmptyState.Footer
 * @internal
 */
export function isEmptyStateFooter(
  child: ReactNode,
): child is ReactElement<EmptyStateFooterProps> {
  return isElement(child) && child.type === Footer;
}

/**
 * The footer slot for the EmptyState component.

* @public
*/
export const Footer = /* @__PURE__ */ forwardRef<
  HTMLElement,
  EmptyStateFooterProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = props;

  return (
    <footer
      ref={forwardedRef}
      className={clsx([
        footerCSS,
        footerSlotCss,
        textStyleCSS({ textStyle: 'base' }),
        consumerClassName,
      ])}
      style={consumerStyle}
      data-testid={dataTestId}
    >
      {children}
    </footer>
  );
});

(Footer as typeof Footer & { displayName: string }).displayName =
  'EmptyState.Footer';
